import { useContext } from 'react';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { BoostIconDisplay } from 'src/components/SponsoredListings/BoostIconDisplay';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog/useConfirmDialog';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import {
  BulkBoostModal,
  useBulkBoostActions,
} from 'src/dialogs/BulkEdits/BulkBoostEvents';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, Permission } from 'src/WebApiController';

export const BoostActions = () => {
  const hasBulkBoostFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_BulkBoost
  );
  const hasAdPlatformCreatePermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsCreate
  );
  const { areBulkActionsEnabled } = useBulkActionsState();
  const { setModal } = useContext(ModalContext);
  const { openConfirmDialog, closeConfirmDialog } = useConfirmDialog();

  const {
    unboostedCount,
    pausedCount,
    activeCount,
    ineligibleCount,
    unboostedEvents,
    pausedEvents,
    activeEvents,
    ineligibleEvents,
    executeBulkBoost,
    executeBulkPause,
  } = useBulkBoostActions();

  if (!hasBulkBoostFeature || !hasAdPlatformCreatePermission) {
    return null;
  }

  const handleBoostClick = () => {
    setModal({
      children: (
        <BulkBoostModal
          unboostedCount={unboostedCount}
          pausedCount={pausedCount}
          activeCount={activeCount}
          ineligibleCount={ineligibleCount}
          unboostedEvents={unboostedEvents}
          pausedEvents={pausedEvents}
          activeEvents={activeEvents}
          ineligibleEvents={ineligibleEvents}
          onExecute={executeBulkBoost}
        />
      ),
      clickOutsideToClose: true,
      size: 'slide-in-md',
    });
  };

  const handlePauseClick = () => {
    openConfirmDialog({
      headerText: 'Pause Boost',
      bodyText: `${activeCount} event(s) will have their boost paused.`,
      onOkay: async () => {
        await executeBulkPause();
        closeConfirmDialog();
      },
      onCancel: closeConfirmDialog,
      okText: ContentId.Pause,
    });
  };

  return (
    <PosDropdown
      key="events-boost-actions-dropdown"
      trigger={
        <Button variant="regular" shape="pill">
          <BoostIconDisplay size={vars.iconSize.m} isSelected />
          <Content id={ContentId.Boost} />
        </Button>
      }
      align="start"
      triggerProps={{ disabled: !areBulkActionsEnabled }}
    >
      <PosDropdownItem onClick={handleBoostClick}>
        <Content id={ContentId.BoostEvent} />
      </PosDropdownItem>
      <PosDropdownItem onClick={handlePauseClick}>
        <Content id={ContentId.Pause} />
      </PosDropdownItem>
    </PosDropdown>
  );
};
